import { useState, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ChoiceGrid, StepFooter, StepHeader } from "./_widgets";
import { base44 } from "@/api/base44Client";
import { Capacitor } from "@capacitor/core";
import { Camera as CapCamera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Camera, ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const YESNO = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const PHOTO_SLOTS = [
  { key: "photo_front", label: "Front" },
  { key: "photo_side", label: "Side" },
  { key: "photo_back", label: "Back" },
];

const IS_NATIVE = Capacitor.isNativePlatform();

function PhotoSlot({ slot, url, onUpload, onRemove }) {
  const [busy, setBusy] = useState(false);
  const [showNativePicker, setShowNativePicker] = useState(false);
  const fileInputRef = useRef(null);

  const uploadFileObject = async (file) => {
    setBusy(true);
    try {
      const uploadRes = await base44.integrations.Core.UploadFile({ file });
      const photoUrl = uploadRes?.url || uploadRes?.file_url;
      if (!photoUrl) {
        throw new Error("No URL returned from upload server.");
      }
      onUpload(slot.key, photoUrl);
      toast.success(`${slot.label} photo uploaded!`);
    } catch (err) {
      console.error(`Failed to upload ${slot.label} photo:`, err);
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to upload photo. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleNativeCapture = async (source) => {
    setShowNativePicker(false);
    try {
      const photo = await CapCamera.getPhoto({
        quality: 85,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source,
        correctOrientation: true,
      });
      if (!photo?.dataUrl) return;
      const res = await fetch(photo.dataUrl);
      const blob = await res.blob();
      const file = new File([blob], `${slot.key}_${Date.now()}.jpg`, {
        type: blob.type || "image/jpeg",
      });
      await uploadFileObject(file);
    } catch (e) {
      const msg = String(e?.message || e);
      if (!/cancel/i.test(msg)) {
        console.error("Camera error:", e);
        toast.error(msg || "Could not open camera.");
      }
    }
  };

  const handleWebChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFileObject(file);
    if (e.target) e.target.value = "";
  };

  const handleClickSlot = (e) => {
    if (busy) return;
    if (IS_NATIVE) {
      e.preventDefault();
      setShowNativePicker(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  return (
    <>
      <div
        className="relative flex flex-col items-center justify-center aspect-[3/4] rounded-xl border-2 border-dashed border-border bg-muted/30 cursor-pointer overflow-hidden hover:border-primary/50 transition-colors select-none"
        onClick={handleClickSlot}
        data-testid={`onb-photo-${slot.key}`}
      >
        {url ? (
          <>
            <img
              src={url}
              alt={slot.label}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(slot.key);
              }}
              className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-6 h-6 rounded-full bg-black/70 text-white hover:bg-black transition-colors"
              aria-label="Remove"
              data-testid={`onb-photo-${slot.key}-remove`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] font-semibold py-1 text-center uppercase tracking-wider">
              {slot.label}
            </div>
          </>
        ) : busy ? (
          <div className="flex flex-col items-center gap-1.5 p-2 text-center">
            <Loader2 className="w-5 h-5 animate-spin text-primary" />
            <span className="text-[10px] text-muted-foreground font-medium">Uploading...</span>
          </div>
        ) : (
          <>
            <Camera className="w-6 h-6 text-muted-foreground mb-1" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {slot.label}
            </span>
            <span className="text-[10px] text-muted-foreground/70 mt-0.5">Tap to upload</span>
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleWebChange}
          disabled={busy}
          data-testid={`onb-photo-${slot.key}-input`}
        />
      </div>

      {/* Native Photo Source Chooser Dialog */}
      <Dialog open={showNativePicker} onOpenChange={setShowNativePicker}>
        <DialogContent className="sm:max-w-xs p-5" data-testid={`onb-photo-${slot.key}-picker`}>
          <DialogHeader>
            <DialogTitle className="text-base text-center">
              Add {slot.label} Photo
            </DialogTitle>
            <DialogDescription className="text-xs text-center">
              Take a new photo or select one from your library.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2.5 pt-2">
            <Button
              type="button"
              size="lg"
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center gap-2"
              onClick={() => handleNativeCapture(CameraSource.Camera)}
              data-testid={`onb-photo-${slot.key}-camera`}
            >
              <Camera className="w-4 h-4" />
              Take a Photo
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full flex items-center justify-center gap-2"
              onClick={() => handleNativeCapture(CameraSource.Photos)}
              data-testid={`onb-photo-${slot.key}-photos`}
            >
              <ImageIcon className="w-4 h-4" />
              Choose from Library
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={() => setShowNativePicker(false)}
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Step3Body({ data, set, onNext }) {
  const knows = data.knows_body_fat;
  const photos = data.progress_photos || {};
  const allPhotos = PHOTO_SLOTS.every((s) => !!photos[s.key]);
  const bfOk = knows !== "yes" || (data.body_fat_pct !== "" && Number(data.body_fat_pct) > 0);
  const waistOk = data.waist !== "";
  const valid = !!knows && bfOk && waistOk && allPhotos;

  const setPhoto = (key, url) => set({ progress_photos: { ...photos, [key]: url } });
  const removePhoto = (key) => {
    const next = { ...photos };
    delete next[key];
    set({ progress_photos: next });
  };

  return (
    <div className="space-y-5">
      <StepHeader title="Body Measurements" subtitle="Baseline metrics so we can show progress." />

      <div className="space-y-1.5">
        <Label>Do you know your body fat percentage?</Label>
        <ChoiceGrid
          options={YESNO}
          value={knows}
          onChange={(v) => set({ knows_body_fat: v })}
          columns={2}
          testid="onb-knows-bf"
        />
      </div>

      {knows === "yes" && (
        <div className="space-y-1.5">
          <Label>Estimated body fat %</Label>
          <Input
            type="number"
            min={3}
            max={60}
            placeholder="e.g. 18"
            value={data.body_fat_pct}
            data-testid="onb-bf"
            onChange={(e) => set({ body_fat_pct: e.target.value })}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Waist measurement (in)</Label>
        <Input
          type="number"
          min={15}
          max={80}
          placeholder="e.g. 32"
          value={data.waist}
          data-testid="onb-waist"
          onChange={(e) => set({ waist: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Progress photos</Label>
        <p className="text-[11px] text-muted-foreground -mt-1 mb-2">
          Good lighting · relaxed posture · consistent clothing
        </p>
        <div className="grid grid-cols-3 gap-2">
          {PHOTO_SLOTS.map((s) => (
            <PhotoSlot
              key={s.key}
              slot={s}
              url={photos[s.key]}
              onUpload={setPhoto}
              onRemove={removePhoto}
            />
          ))}
        </div>
      </div>

      <StepFooter valid={valid} onNext={onNext} />
    </div>
  );
}
